import { useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, Eye, EyeOff, LockKeyhole, Mail } from "lucide-react";

import { API_BASE, LOGIN_ENDPOINT } from "../config";
import { setAuthFromLogin } from "../api/Client";
import styles from "./LoginPage.module.css";

type TenantOption = {
  membershipKey: string;
  tenantId: number;
  tenantName: string;
  tenantSlug?: string | null;
  customerName?: string;
};

type TenantSelectionState = {
  selectionToken: string;
  options: TenantOption[];
};

type LoginResponse = {
  status?: string;
  selectionToken?: string;
  options?: TenantOption[];
  accessToken?: string;
  refreshToken?: string;
  user?: unknown;
  error?: string;
};

export default function LoginPage({ onLoginSuccess }: { onLoginSuccess?: () => void }) {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [tenantSelection, setTenantSelection] = useState<TenantSelectionState | null>(null);
  const [selectingMembershipKey, setSelectingMembershipKey] = useState<string | null>(null);

  const canSubmit = useMemo(
    () => email.trim().length > 0 && password.trim().length > 0 && !submitting,
    [email, password, submitting]
  );

  async function finalizeAuthenticatedSession(payload: LoginResponse) {
    await setAuthFromLogin(payload);
    if (onLoginSuccess) {
      await onLoginSuccess();
    }
    navigate("/product", { replace: true });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}${LOGIN_ENDPOINT}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
        }),
      });

      const data = (await response.json().catch(() => ({}))) as LoginResponse;
      if (!response.ok) {
        throw new Error(data.error || "No se pudo iniciar sesion");
      }

      if (data.status === "select_tenant") {
        const options = Array.isArray(data.options) ? data.options : [];
        const selectionToken = String(data.selectionToken || "");
        if (!selectionToken || options.length === 0) {
          throw new Error("No se pudo completar la seleccion de negocio");
        }
        setTenantSelection({ selectionToken, options });
        return;
      }

      await finalizeAuthenticatedSession(data);
    } catch (requestError: any) {
      setError(requestError?.message || "No se pudo iniciar sesion");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleTenantSelection(option: TenantOption) {
    if (!tenantSelection) {
      return;
    }

    setError(null);
    setSelectingMembershipKey(option.membershipKey);
    try {
      const response = await fetch(`${API_BASE}/api/customers/auth/select-tenant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          selectionToken: tenantSelection.selectionToken,
          membershipKey: option.membershipKey,
        }),
      });

      const data = (await response.json().catch(() => ({}))) as LoginResponse;
      if (!response.ok) {
        throw new Error(data.error || "No se pudo completar el acceso");
      }

      setTenantSelection(null);
      await finalizeAuthenticatedSession(data);
    } catch (requestError: any) {
      setError(requestError?.message || "No se pudo completar el acceso");
    } finally {
      setSelectingMembershipKey(null);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.topBand} aria-hidden="true" />
      <div className={styles.bgGlow} aria-hidden="true" />
      <div className={styles.bgMesh} aria-hidden="true" />

      <main className={styles.layout}>
        <section className={styles.panel}>
          <div className={styles.brandLockup}>
            <div className={styles.logoMark}>F</div>
            <div className={styles.brandText}>
              <h1>Fidelis</h1>
              <p>Tu espacio para entrenar, reservar y seguir avanzando.</p>
            </div>
          </div>

          <div className={styles.panelHeader}>
            <span className={styles.kicker}>Acceso clientes</span>
            <h2>Inicia sesion</h2>
            <p>Accede a tus productos, reservas y progreso en tu centro.</p>
          </div>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <label className={styles.field}>
              <span>Correo electronico</span>
              <div className={styles.inputWrap}>
                <Mail size={18} />
                <input
                  className={styles.input}
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="tu@correo.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </div>
            </label>

            <label className={styles.field}>
              <span>Contrasena</span>
              <div className={styles.inputWrap}>
                <LockKeyhole size={18} />
                <input
                  className={styles.input}
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Tu contrasena"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? "Ocultar contrasena" : "Mostrar contrasena"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </label>

            {error ? <p className={styles.error}>{error}</p> : null}

            <button type="submit" className={styles.submit} disabled={!canSubmit}>
              {submitting ? "Accediendo..." : "Continuar"}
              <ArrowRight size={16} />
            </button>

            <div className={styles.formFooter}>
              <button
                type="button"
                className={styles.recover}
                onClick={() => navigate("/recover-password")}
              >
                Has olvidado tu contrasena?
              </button>

              <button
                type="button"
                className={styles.catalogLink}
                onClick={() => navigate("/public-services")}
              >
                Ver servicios y productos
              </button>
            </div>
          </form>
        </section>
      </main>

      {tenantSelection ? (
        <div className={styles.tenantBackdrop} role="presentation">
          <div className={styles.tenantModal} role="dialog" aria-modal="true" aria-labelledby="tenant-title">
            <div className={styles.tenantHeader}>
              <div>
                <span className={styles.tenantKicker}>Seleccion de negocio</span>
                <h3 id="tenant-title">Elige en que centro quieres entrar</h3>
              </div>
              <button
                type="button"
                className={styles.closeTenant}
                onClick={() => {
                  if (!selectingMembershipKey) {
                    setTenantSelection(null);
                  }
                }}
                aria-label="Cerrar seleccion de centro"
              >
                X
              </button>
            </div>

            <p className={styles.tenantCopy}>
              Tu cuenta esta asociada a varios centros. Selecciona uno para crear la sesion en ese tenant.
            </p>

            <div className={styles.tenantList}>
              {tenantSelection.options.map((option) => (
                <button
                  key={option.membershipKey}
                  type="button"
                  className={styles.tenantOption}
                  onClick={() => {
                    void handleTenantSelection(option);
                  }}
                  disabled={Boolean(selectingMembershipKey)}
                >
                  <span className={styles.tenantIcon}>
                    <Building2 size={17} />
                  </span>
                  <span className={styles.tenantInfo}>
                    <strong>{option.tenantName}</strong>
                    <span>{option.customerName || email.trim().toLowerCase()}</span>
                  </span>
                  <span className={styles.tenantAction}>
                    {selectingMembershipKey === option.membershipKey ? "Abriendo..." : "Entrar"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
